import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { dltService } from "../services/DltService";
import withAuthorization from "../components/withAuthorization";
import AlertModal from "../components/AlertModal";
import LoadingModal from "../components/LoadingModal";
import Button2 from "../components/Button2";
import { ReactComponent as BackIcon } from "../icons/back.svg";
import "../styles/DltCorrectionPage.css";

const DltCorrectionPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertTitle, setAlertTitle] = useState("DLT Correction");
  const [successResult, setSuccessResult] = useState(null);
  const [isMerchantMode, setIsMerchantMode] = useState(false);

  const [formData, setFormData] = useState({
    pdlNumber: "",
    currentAmount: "",
    correctAmount: "",
    reason: "",
    approvedBy: "",
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const getPdlLabel = () => {
    return isMerchantMode ? "Store Code" : "PDL Number";
  };

  const getPdlPlaceholder = () => {
    return isMerchantMode ? "e.g., STORE001" : "e.g., 132425-P";
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.pdlNumber.trim()) {
      newErrors.pdlNumber = `${getPdlLabel()} is required`;
    }

    if (!formData.currentAmount.trim()) {
      newErrors.currentAmount = "Current Amount is required";
    } else if (isNaN(formData.currentAmount) || parseFloat(formData.currentAmount) < 0) {
      newErrors.currentAmount = "Please enter a valid amount";
    }

    if (!formData.correctAmount.trim()) {
      newErrors.correctAmount = "Correct Amount is required";
    } else if (isNaN(formData.correctAmount) || parseFloat(formData.correctAmount) < 0) {
      newErrors.correctAmount = "Please enter a valid amount";
    }

    if (!formData.reason.trim()) {
      newErrors.reason = "Reason for correction is required";
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason = "Reason must be at least 10 characters long";
    }

    if (!formData.approvedBy.trim()) {
      newErrors.approvedBy = "Approved By email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.approvedBy)) {
      newErrors.approvedBy = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        pdlNumber: formData.pdlNumber.trim(),
        currentAmount: parseFloat(formData.currentAmount),
        correctAmount: parseFloat(formData.correctAmount),
        reason: formData.reason.trim(),
        approvedBy: formData.approvedBy.trim(),
      };

      const response = await dltService.postCorrectData(null, payload, isMerchantMode);

      setSuccessResult(response?.data);
      setAlertTitle("Success");
      setAlertMessage("Record correction submitted successfully");
      setShowAlert(true);

      // Reset form
      setFormData({
        pdlNumber: "",
        currentAmount: "",
        correctAmount: "",
        reason: "",
        approvedBy: "",
      });
    } catch (error) {
      setAlertTitle("Error");
      setAlertMessage(error?.response?.data?.message || "Failed to submit correction request");
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      pdlNumber: "",
      currentAmount: "",
      correctAmount: "",
      reason: "",
      approvedBy: "",
    });
    setErrors({});
  };

  const handleNewCorrection = () => {
    setSuccessResult(null);
    handleReset();
  };

  return (
    <div className="dlt-correction-container">
      <AlertModal
        show={showAlert}
        title={alertTitle}
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
      <LoadingModal show={loading} message="Processing correction request..." />

      <div className="correction-header">
        <div className="header-back">
          <BackIcon
            style={{ cursor: "pointer", width: "50px", height: "50px" }}
            onClick={() => navigate("/dashboard")}
          />
        </div>
        <div className="header-title">
          <h1>DLT Record Correction</h1>
          <p>Correct tampered or inaccurate distributed ledger records</p>
        </div>
      </div>

      <div className="correction-content">
        {!successResult ? (
          <div className="correction-form-section">
            <div className="form-card">
              <div className="form-header">
                <h2>Correction Form</h2>
                <p>Fill in the details below to submit a record correction request</p>
              </div>

              {/* Merchant Correction Toggle */}
              <div className="merchant-mode-toggle-section">
                <label className="merchant-mode-toggle">
                  <input
                    type="checkbox"
                    checked={isMerchantMode}
                    onChange={(e) => setIsMerchantMode(e.target.checked)}
                    className="merchant-mode-toggle-input"
                  />
                  <span className="merchant-mode-toggle-slider"></span>
                  <span className="merchant-mode-toggle-label">Merchant Correction</span>
                </label>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  {/* PDL Number / Store Code */}
                  <div className="form-group">
                    <label htmlFor="pdlNumber" className="form-label">
                      {getPdlLabel()} <span className="required">*</span>
                    </label>
                    <input
                      id="pdlNumber"
                      type="text"
                      name="pdlNumber"
                      placeholder={getPdlPlaceholder()}
                      value={formData.pdlNumber}
                      onChange={handleInputChange}
                      className={`form-input ${errors.pdlNumber ? "error" : ""}`}
                    />
                    {errors.pdlNumber && <div className="error-message">{errors.pdlNumber}</div>}
                  </div>

                  {/* Current Amount */}
                  <div className="form-group">
                    <label htmlFor="currentAmount" className="form-label">
                      Current Amount <span className="required">*</span>
                    </label>
                    <input
                      id="currentAmount"
                      type="number"
                      name="currentAmount"
                      placeholder="0.00"
                      value={formData.currentAmount}
                      onChange={handleInputChange}
                      className={`form-input ${errors.currentAmount ? "error" : ""}`}
                      step="0.01"
                      min="0"
                    />
                    {errors.currentAmount && <div className="error-message">{errors.currentAmount}</div>}
                  </div>

                  {/* Correct Amount */}
                  <div className="form-group">
                    <label htmlFor="correctAmount" className="form-label">
                      Correct Amount <span className="required">*</span>
                    </label>
                    <input
                      id="correctAmount"
                      type="number"
                      name="correctAmount"
                      placeholder="0.00"
                      value={formData.correctAmount}
                      onChange={handleInputChange}
                      className={`form-input ${errors.correctAmount ? "error" : ""}`}
                      step="0.01"
                      min="0"
                    />
                    {errors.correctAmount && <div className="error-message">{errors.correctAmount}</div>}
                  </div>

                  {/* Approved By */}
                  <div className="form-group">
                    <label htmlFor="approvedBy" className="form-label">
                      Approved By (Email) <span className="required">*</span>
                    </label>
                    <input
                      id="approvedBy"
                      type="email"
                      name="approvedBy"
                      placeholder="user@example.com"
                      value={formData.approvedBy}
                      onChange={handleInputChange}
                      className={`form-input ${errors.approvedBy ? "error" : ""}`}
                    />
                    {errors.approvedBy && <div className="error-message">{errors.approvedBy}</div>}
                  </div>

                  {/* Reason - Full Width */}
                  <div className="form-group full-width">
                    <label htmlFor="reason" className="form-label">
                      Reason for Correction <span className="required">*</span>
                    </label>
                    <textarea
                      id="reason"
                      name="reason"
                      placeholder="Describe the reason for this correction (minimum 10 characters)"
                      value={formData.reason}
                      onChange={handleInputChange}
                      className={`form-textarea ${errors.reason ? "error" : ""}`}
                      rows="4"
                    />
                    <div className="char-count">
                      {formData.reason.length}/500 characters
                    </div>
                    {errors.reason && <div className="error-message">{errors.reason}</div>}
                  </div>
                </div>

                {/* Difference Display */}
                {formData.currentAmount && formData.correctAmount && (
                  <div className="difference-display">
                    <div className="difference-item">
                      <span className="label">Difference Amount:</span>
                      <span className={`value ${parseFloat(formData.correctAmount) > parseFloat(formData.currentAmount) ? "positive" : "negative"}`}>
                        {Math.abs(parseFloat(formData.correctAmount) - parseFloat(formData.currentAmount)).toFixed(2)}
                      </span>
                    </div>
                    <div className="difference-item">
                      <span className="label">Direction:</span>
                      <span className={`value ${parseFloat(formData.correctAmount) > parseFloat(formData.currentAmount) ? "positive" : "negative"}`}>
                        {parseFloat(formData.correctAmount) > parseFloat(formData.currentAmount) ? "Increase" : "Decrease"}
                      </span>
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="form-actions">
                  <button type="submit" className="btn-submit">
                    {loading ? "Processing..." : "Submit Correction"}
                  </button>
                  <button type="button" className="btn-reset" onClick={handleReset}>
                    Clear Form
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="success-section">
            <div className="success-card">
              <div className="success-icon">✓</div>
              <h2>Correction Request Submitted</h2>
              <p>Your record correction has been successfully submitted.</p>

              {successResult && (
                <div className="result-details">
                  <div className="detail-item">
                    <span className="detail-label">{getPdlLabel()}:</span>
                    <span className="detail-value">{successResult.pdlNumber}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Current Amount:</span>
                    <span className="detail-value">{successResult.currentAmount?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Correct Amount:</span>
                    <span className="detail-value">{successResult.correctAmount?.toFixed(2)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Reason:</span>
                    <span className="detail-value">{successResult.reason}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Approved By:</span>
                    <span className="detail-value">{successResult.approvedBy}</span>
                  </div>
                </div>
              )}

              <div className="success-actions">
                <Button2 text="Submit Another Correction" onClick={handleNewCorrection} />
                <button className="btn-dashboard" onClick={() => navigate("/dashboard")}>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default withAuthorization(DltCorrectionPage, 40);
